/**
 * Keyboard hints for the S0 chord-presentation view.
 *
 * S0 owns its own hint source (`'journey-s0'`) because "which chord is
 * current" is a view concern: it follows playback position, or the cell
 * the player points at. See the header of `journey-hint-mapping.ts`.
 *
 * The set is always **one chord**, never the whole phrase. Lighting every
 * note of the phrase at once was the original defect: the keyboard showed
 * four chords merged while the panel claimed one of them was current.
 */

import type { HintNote } from "@/contexts/hint-context";
import {
  JOURNEY_HINT_COLOR_LEFT_PRACTICE,
  JOURNEY_HINT_COLOR_RIGHT_PRACTICE,
} from "@/hooks/journey/journey-hint-mapping";
import type { PhraseChord } from "./phrase-chords";

/**
 * Dimmed hand colors for notes held over from the previous chord.
 *
 * Same hue, roughly a third of the luminance, so the eye reads them as
 * "the same hand, but not a new action". This teaches something the chord
 * symbol hides entirely: you don't re-strike, you keep the finger down.
 */
const HELD_COLOR_LEFT = 0x1a5751;
const HELD_COLOR_RIGHT = 0x4a3d6b;

/**
 * The same hand colors as CSS, for the panel's little keyboards.
 *
 * They deliberately match what the studio keyboard paints underneath
 * (`JOURNEY_HINT_COLOR_*_PRACTICE`): a player who learns "turquoise is my
 * left hand" on the big keyboard must not have to relearn it two
 * centimetres above.
 */
export const HAND_CSS_COLORS = {
  left: "#40d9c9",
  right: "#b597ff",
  leftHeld: "#1a5751",
  rightHeld: "#4a3d6b",
} as const;

/**
 * Per-midi colors for a `MiniPiano`, painted by hand.
 *
 * Without this every highlighted key is washed in one accent, so a
 * two-bar chord window reads as an undifferentiated block of a dozen
 * keys. Split by hand, the same drawing separates into accompaniment and
 * melody at a glance.
 */
export function chordHandColors(
  entry: PhraseChord,
): Map<number, string> {
  const colors = new Map<number, string>();

  // Right wins on a shared midi, and struck wins over held: an explicit
  // attack is the more actionable of the two. Assign in reverse priority.
  for (const midi of entry.left.sustainedMidis) {
    colors.set(midi, HAND_CSS_COLORS.leftHeld);
  }

  for (const midi of entry.right.sustainedMidis) {
    colors.set(midi, HAND_CSS_COLORS.rightHeld);
  }

  for (const midi of entry.left.midis) {
    colors.set(midi, HAND_CSS_COLORS.left);
  }

  for (const midi of entry.right.midis) {
    colors.set(midi, HAND_CSS_COLORS.right);
  }

  return colors;
}

/**
 * Hints for one chord: struck notes at full hand color, held notes dimmed.
 *
 * Right hand wins on a midi shared by both hands, the same convention as
 * `currentGroupHints`. Struck wins over held for the same reason: an
 * explicit attack is the more actionable of the two.
 */
export function chordHints(entry: PhraseChord | null): HintNote[] {
  if (entry === null) {
    return [];
  }

  const out: HintNote[] = [];
  const seen = new Set<number>();

  const push = (midi: number, color: number): void => {
    if (seen.has(midi)) {
      return;
    }

    seen.add(midi);
    out.push({ midi, color });
  };

  for (const midi of entry.right.midis) {
    push(midi, JOURNEY_HINT_COLOR_RIGHT_PRACTICE);
  }

  for (const midi of entry.left.midis) {
    push(midi, JOURNEY_HINT_COLOR_LEFT_PRACTICE);
  }

  for (const midi of entry.right.sustainedMidis) {
    push(midi, HELD_COLOR_RIGHT);
  }

  for (const midi of entry.left.sustainedMidis) {
    push(midi, HELD_COLOR_LEFT);
  }

  return out;
}
